import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DATE_FORMAT, ROUTES } from "@/shared/constants";
import { ExtendedComponents } from "@/components/extended";
import { SharedNotificationDropdown } from "@/components/NotificationDropdown";
import { formatDateToTZ } from "@/shared/utils/date.util";
import {
  DataValidationNotification,
  DataValidationNotificationDropdownProps,
} from "../types/DataValidationNotification.type";
import { updateDataValidationNotificationStatus } from "../services/dataValidationNotificationService";
import { showToastDynamic } from "@/shared/utils/toast.util";

const DataValidationNotificationDropdown: React.FC<
  DataValidationNotificationDropdownProps
> = ({ isOpen, onClose, notifications, onUpdated }): React.ReactElement => {
  const navigate = useNavigate();

  const handleUpdateAndNavigate = useCallback(
    async (ids: number[]): Promise<void> => {
      try {
        const response = await updateDataValidationNotificationStatus({
          ids,
        });

        if (!response.success) {
          showToastDynamic(
            response.message || "Failed to update notification status",
            "error",
          );
          return;
        }

        onUpdated();
        navigate(ROUTES.reporting.validationHistory);
        onClose();
      } catch {
        showToastDynamic("Failed to update notification status", "error");
      }
    },
    [navigate, onClose, onUpdated],
  );

  const handleNotificationItemClick = useCallback(
    (notification: DataValidationNotification): void => {
      void handleUpdateAndNavigate([notification.id]);
    },
    [handleUpdateAndNavigate],
  );

  const handleViewAllClick = useCallback(async (): Promise<void> => {
    await handleUpdateAndNavigate([]);
  }, [handleUpdateAndNavigate]);

  const hasPendingNotifications = notifications.length > 0;

  const visibleNotifications = notifications.slice(0, 3);

  return (
    <SharedNotificationDropdown<DataValidationNotification>
      isOpen={isOpen}
      onClose={onClose}
      title="Data Validation Notifications"
      emptyMessage="No pending notifications"
      items={hasPendingNotifications ? visibleNotifications : []}
      getItemKey={(notification): number => notification.id}
      onItemClick={handleNotificationItemClick}
      onViewAll={() => void handleViewAllClick()}
      renderItem={(notification): React.ReactNode => (
        <>
          {/* DV Name */}
          <p className="mb-1 min-w-0 text-sm font-medium text-foreground line-clamp-2 whitespace-normal [overflow-wrap:anywhere]">
            {notification.dv_name}
          </p>

          {/* Datetime + Badges */}
          <div className="flex w-full min-w-0 justify-between gap-3">
            <span className="min-w-0 text-xs text-muted-foreground break-words [overflow-wrap:anywhere]">
              {formatDateToTZ(
                notification.dv_executed_datetime,
                DATE_FORMAT.dateTime,
              )}
            </span>

            {/* Success and Error Badges */}
            <div className="flex shrink-0 items-center gap-2">
              {/* Success Badge */}
              <ExtendedComponents.customTooltip
                triggerChild={
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-success text-white text-xs font-semibold">
                    {notification.success_count}
                  </div>
                }
                triggerContent={`Success: ${notification.success_count} records`}
                side="top"
              />

              {/* Error Badge */}
              <ExtendedComponents.customTooltip
                triggerChild={
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-destructive text-white text-xs font-semibold">
                    {notification.error_count}
                  </div>
                }
                triggerContent={`Error: ${notification.error_count} records`}
                side="top"
              />
            </div>
          </div>
        </>
      )}
    />
  );
};

export default DataValidationNotificationDropdown;
